import React, { useState } from 'react';
import { Box, Typography, TextField, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './CustomerTable.css';

const CustomerTable = ({ entities, isEntitiesLoading, searchQuery, setSearchQuery, handleEntitySelect, theme = 'dark' }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'applicant_name', direction: 'asc' });
  const navigate = useNavigate();

  const filteredEntities = entities
    .filter(
      (entity) =>
        entity &&
        entity.id &&
        (entity.applicant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          entity.account_no?.includes(searchQuery) ||
          entity.branch?.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .map((entity, index) => ({
      ...entity,
      serialNo: index + 1,
    }));

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    filteredEntities.sort((a, b) => {
      const aVal = a[key] || '';
      const bVal = b[key] || '';
      return aVal < bVal
        ? direction === 'asc' ? -1 : 1
        : aVal > bVal
        ? direction === 'asc' ? 1 : -1
        : 0;
    });
  };

  const handleViewDocuments = async (entityId) => {
    console.log('Navigating to documents page for entity ID:', entityId);
    
    if (!entityId) {
      toast.error('Invalid entity ID', {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          background: theme === 'dark' ? '#f8d7da' : '#fee2e2',
          color: '#721c24',
          borderRadius: '8px',
          fontSize: '14px',
        },
      });
      return;
    }

    // Simply navigate with the entity ID - ExistingCustomerDocs will fetch all data
    navigate(`/customer-documents/${entityId}`);
  };

  return (
    <Box className={`customer-table-browse-section ${theme}-theme`}>
      <Typography variant="h6" className="customer-table-section-title">
        Existing Profiles
      </Typography>
      <TextField
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search by Name, Account"
        variant="outlined"
        fullWidth
        className="customer-table-search-input"
      />
      
      {isEntitiesLoading ? (
        <Box className="customer-table-loading-container">
          <CircularProgress className="customer-table-loading-spinner" />
        </Box>
      ) : filteredEntities.length === 0 ? (
        <Box className="customer-table-no-profiles card">
          <Typography variant="body1" className="customer-table-no-profiles-text">
            No profiles found.
          </Typography>
        </Box>
      ) : (
        <Box className="customer-table-container card">
          <Table className="customer-table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'serialNo'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('serialNo')}
                    sx={{ color: 'var(--text-color) !important', '&.Mui-active': { color: 'var(--text-color)' } }}
                  >
                    Serial No
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'applicant_name'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('applicant_name')}
                    sx={{ color: 'var(--text-color) !important', '&.Mui-active': { color: 'var(--text-color)' } }}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'account_no'}
                    direction={sortConfig.direction}
                    onClick={() => handleSort('account_no')}
                    sx={{ color: 'var(--text-color) !important', '&.Mui-active': { color: 'var(--text-color)' } }}
                  >
                    Account Number
                  </TableSortLabel>
                </TableCell>
                <TableCell>Customer Documents</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntities.map((entity) => (
                <TableRow key={entity.id}>
                  <TableCell>{entity.serialNo}</TableCell>
                  <TableCell>{entity.applicant_name || 'Unknown'}</TableCell>
                  <TableCell>{entity.account_no || '-'}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DescriptionIcon />}
                      onClick={() => handleViewDocuments(entity.id)}
                      className="customer-table-documents-button btn-outlined"
                    >
                      View Documents
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleEntitySelect(entity)}
                      className="customer-table-view-button btn-outlined"
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default CustomerTable;